import { FC, ReactNode } from 'react';
import { ShieldCheck, Lock } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@/utils/currency';
import { OrderSummaryModel } from '../types';

interface Props {
  summary: OrderSummaryModel;
  note?: ReactNode;
}

const OrderSummary: FC<Props> = ({ summary, note }) => {
  const { t, i18n } = useTranslation(['checkout']);

  const { lines, subtotal, taxes, total } = summary;
  const hasTaxes = taxes.length > 0;

  return (
    <div className="rounded-xl border bg-white p-6 shadow-sm">
      <h2 className="text-xl font-bold mb-5">{t('checkout:order-summary-title', 'Order Summary')}</h2>

      {lines.map((line, i) =>
        line.amount === undefined ? (
          <div key={i} className="mb-1 last:mb-0">
            <p className="text-sm text-muted-foreground">{line.label}</p>
            {line.sublabel && (
              <p className="text-xs text-muted-foreground">{line.sublabel}</p>
            )}
          </div>
        ) : (
          <div key={i} className="flex items-start justify-between gap-4 text-sm mb-2">
            <span className="text-muted-foreground min-w-0">
              {line.label}
              {line.sublabel && (
                <span className="block text-xs">{line.sublabel}</span>
              )}
            </span>
            <span className="shrink-0">{formatCurrency(line.amount, i18n.language)}</span>
          </div>
        ),
      )}

      <Separator className="my-4" />

      {hasTaxes && (
        <>
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-muted-foreground">{t('checkout:order-summary-subtotal', 'Subtotal')}</span>
            <span>{formatCurrency(subtotal, i18n.language)}</span>
          </div>

          {taxes.map((tax) => (
            <div key={tax.name} className="flex items-center justify-between text-sm mb-2">
              <span className="text-muted-foreground">
                {tax.code} ({tax.rate}%)
              </span>
              <span>{formatCurrency(tax.amount, i18n.language)}</span>
            </div>
          ))}

          <Separator className="my-3" />
        </>
      )}

      <div className="flex items-center justify-between font-bold text-base mt-2">
        <span>{t('checkout:order-summary-total', 'Total')}</span>
        <span>{formatCurrency(total, i18n.language)}</span>
      </div>

      {note}

      <div className="mt-6 space-y-2">
        <div className="flex items-center gap-2 text-sm text-emerald-700">
          <ShieldCheck className="size-4 shrink-0" />
          <span>{t('checkout:order-summary-ssl-secured', 'SSL Secured Payment')}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-emerald-700">
          <Lock className="size-4 shrink-0" />
          <span>{t('checkout:order-summary-pci-compliant', 'PCI DSS Compliant')}</span>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
